/**
 * Экраны приложения и роли, к которым они относятся.
 *
 * Роль — не украшение: от неё зависит нижняя навигация (ТЗ §14.3, «Разная нижняя навигация
 * для клиента и бизнеса»). Поэтому роль указывается таблицей `ROLE_OF_ROUTE`, а не
 * угадывается по префиксу имени: опечатка в строке дала бы клиенту навигацию кабинета.
 *
 * ТОЧКА РАСШИРЕНИЯ ДЛЯ КАБИНЕТА БИЗНЕСА.
 * Чтобы добавить его экраны, нужно ровно три правки и ни одной переделки:
 *   1. описать `BusinessRoute` здесь (сегодня это `never` — экранов кабинета нет);
 *   2. дописать их в `ROLE_OF_ROUTE` (компилятор потребует: `Record` по `RouteName` полный);
 *   3. заполнить `BUSINESS_TABS` в `nav/tabs.ts` и отрисовать экраны в `App.tsx`.
 * Ни маршрутизация, ни API-клиент, ни i18n при этом не трогаются.
 */

/** Клиентская часть (ТЗ §14.1, §14.2). */
export type ClientRoute =
  /** Стартовый экран: «Записаться» или «Принимать записи». Вернулся 21 сентября 2026 по решению владельца. */
  | { readonly name: 'start' }
  | { readonly name: 'catalog' }
  | { readonly name: 'business'; readonly slug: string }
  /** Экран записи: услуга → специалист → день → слот. `serviceId` приходит с карточки услуги. */
  | { readonly name: 'booking'; readonly slug: string; readonly serviceId?: string }
  | { readonly name: 'my' }
  /** Язык и вход в кабинет. Заменил splash 16 сентября 2026. */
  | { readonly name: 'profile' }
  | { readonly name: 'policy' }
  | { readonly name: 'feedback' }
  /** Админка платформы: открыта из профиля только владельцу продукта (`session.isAdmin`). */
  | { readonly name: 'admin' }
  /** Срез по времени конкретного бизнеса в админке (тот же экран, что у владельца). */
  | { readonly name: 'adminStats'; readonly businessId: string };

/**
 * Кабинет бизнеса (ТЗ §14.1, §14.2).
 *
 * `businessId` носится в маршруте, а не лежит в глобальном состоянии: у владельца может
 * быть несколько организаций (`GET /cabinet/businesses` отдаёт список), и «текущая»
 * в одной переменной рано или поздно разъедется с тем, что открыто на экране.
 *
 * `calendar.date` — `YYYY-MM-DD` в поясе организации. Необязательный: пусто означает
 * «сегодня», и этот день считает экран по `tz` организации, а не по часам телефона.
 */
export type BusinessRoute =
  /** Список своих организаций и вход в кабинет. Он же — онбординг, когда организаций нет. */
  | { readonly name: 'bizHome' }
  | { readonly name: 'bizOnboarding' }
  | { readonly name: 'calendar'; readonly businessId: string; readonly date?: string }
  | { readonly name: 'bizSettings'; readonly businessId: string }
  | { readonly name: 'bizServices'; readonly businessId: string }
  | { readonly name: 'bizStaff'; readonly businessId: string }
  | { readonly name: 'bizSchedule'; readonly businessId: string }
  | { readonly name: 'bizTimeOff'; readonly businessId: string }
  | { readonly name: 'bizRules'; readonly businessId: string }
  /** Кого заведение сейчас не принимает и снятие ограничения (ТЗ §19, №23). */
  | { readonly name: 'bizBlacklist'; readonly businessId: string }
  | { readonly name: 'bizPlan'; readonly businessId: string }
  /** Статистика по дням/неделям/месяцам и рейтинг организации. */
  | { readonly name: 'bizStats'; readonly businessId: string };

export type Route = ClientRoute | BusinessRoute;

export type RouteName = Route['name'];

/** Роль определяет набор вкладок внизу. */
export type AppRole = 'client' | 'business';

export const ROLE_OF_ROUTE: Readonly<Record<RouteName, AppRole>> = {
  start: 'client',
  catalog: 'client',
  business: 'client',
  booking: 'client',
  my: 'client',
  profile: 'client',
  policy: 'client',
  feedback: 'client',
  admin: 'client',
  adminStats: 'client',
  bizHome: 'business',
  bizOnboarding: 'business',
  calendar: 'business',
  bizSettings: 'business',
  bizServices: 'business',
  bizStaff: 'business',
  bizSchedule: 'business',
  bizTimeOff: 'business',
  bizRules: 'business',
  bizBlacklist: 'business',
  bizPlan: 'business',
  bizStats: 'business',
};

export function roleOf(route: Route): AppRole {
  return ROLE_OF_ROUTE[route.name];
}

/**
 * Экраны с нижней навигацией — корневые вкладки.
 *
 * Внутренние шаги (страница организации, запись) панель НЕ показывают: во время выбора
 * слота вкладка увела бы клиента с полузаполненной формы одним промахом по краю экрана.
 */
const TAB_ROOTS: readonly RouteName[] = [
  'catalog',
  'my',
  'profile',
  'calendar',
  'bizSettings',
  'bizStats',
  'bizPlan',
];

export function showsTabBar(route: Route): boolean {
  return TAB_ROOTS.includes(route.name);
}

/**
 * Идентификатор организации из маршрута кабинета.
 *
 * `null` на экранах, где организация ещё не выбрана (список, онбординг) и на клиентских.
 * Возвращать сюда «первую попавшуюся» нельзя: экран настроек чужой организации выглядел бы
 * рабочим ровно до первого сохранения.
 */
export function businessIdOf(route: Route): string | null {
  return 'businessId' in route ? route.businessId : null;
}
